import {
  createAsyncThunk,
  createSelector,
  createSlice
} from '@reduxjs/toolkit'
import { slice as profileSlice } from '@polleverywhere/redux/slices/profile'
import { getDefaultPage } from '../pages/getDefaultPage'
import {
  getSlice,
  selectItemType
} from '@polleverywhere/redux/slices/explorer'
import { pluralize } from '@polleverywhere/core/lib/util'
import { explorer } from '../apps/activities/slice'
import { peFetch } from '@polleverywhere/fetch'
import env from '@polleverywhere/core/lib/env'

const sliceName = 'app'
const selectors = {}
const thunks = {}

const actionExplorer = getSlice('actionExplorer')
const notificationTypes = {
  error: 'error',
  info: 'info',
  success: 'success',
  warning: 'warning'
}
const secondaryDomainAuthStatuses = {
  pending: 'pending',
  failed: 'failed',
  ok: 'ok'
}

selectors.selectAutomaticPresenterUpgrade = createSelector(
  (state) => state[sliceName].role,
  (role) => !(role === 'participant' || role === 'embed')
)

// Determine if we have a session started, ensuring we aren't in-progress checking other domains
selectors.selectIsSessionStarted = createSelector(
  (state) => state[profileSlice.name].participant?.id,
  (state) => state[sliceName].secondaryDomainAuth,
  (state) => state[sliceName].options.extraDomainAuth,
  (id, domainAuthStatus, extraDomainAuth) => !!id && (!extraDomainAuth || domainAuthStatus !== secondaryDomainAuthStatuses.pending)
)

// Detect when we're 'fully' logged in, which includes being authenticated with any
// secondary app domains (e.g. viz. which is required for integrations apps with new viz)
selectors.selectIsFullyLoggedIn = createSelector(
  profileSlice.selectIsLoggedIn,
  (state) => state[sliceName].secondaryDomainAuth,
  (state) => state[sliceName].options.extraDomainAuth,
  (isLoggedIn, domainAuthStatus, extraDomainAuth) => !!isLoggedIn && (!extraDomainAuth || domainAuthStatus === secondaryDomainAuthStatuses.ok)
)

// Check the secondary (viz) domain is logged in to the same account as the primary (rails) domain
thunks.checkFullyLoggedIn = createAsyncThunk(
  `${sliceName}/checkFullyLoggedIn`,
  async () => {
    const checks = await Promise.all([
      env.get('api_url').pathname('/sessions/check'),
      env.get('viz_url').pathname('/sessions/check')
    ].map(async url => (await peFetch(url)).json()))

    if (checks[0].user_id) {
      // Ensure all domains are authenticated, with customs, with the same user ID
      return checks.every(c => c.authenticated && c.customs && c.user_id === checks[0].user_id)
    }
    return false
  }
)

const initialNotification = () => ({
  type: null,
  message: null,
  content: null
})

const initialState = {
  options: {
    // Router is optional
    router: true,
    pushState: true,

    // allow signups
    allowSignups: true,
    // Allow reporting, disabled by apps like agave and slideware
    allowReports: true,
    // Allow trash
    allowTrash: true,

    // allow redirecting to other sites such as presenter.polleverywhere.com
    // certain platforms do not allow redirects such as Cordova
    allowRedirects: true,

    // Enables additional logging
    debug: false,

    // Auth token used in SSO flows from rails - used to start up a pre-authed session
    sessionAuthToken: false,

    // enable/disable the session starting on startApp
    shouldStartSessionOnStart: true,

    // Should we try to install the service worker
    serviceWorker: true,

    // Accepted values:
    // false - disable all analytics tracking
    // 'consent' - requires explict consent via prompt
    // true - enables tracking without the need of consent
    allowAnalyticsTracking: true,

    // Does this app use the customs out-of-band flow
    outOfBandAuth: false,
    // If we need to be logged into other domains (e.g. viz.)
    extraDomainAuth: false
  },
  mode: 'standalone',
  role: undefined,
  page: undefined,
  defaultPage: undefined,
  previousPage: undefined,
  ready: false,
  action: {
    args: {},
    explorer: actionExplorer.name,
    name: '',
    opened: false
  },
  dialog: {
    args: {},
    name: '',
    opened: false
  },
  notification: initialNotification(),
  showL1: true,
  showL2: true,
  openL2: true,
  externalScreenConnected: false,
  secondaryDomainAuth: secondaryDomainAuthStatuses.failed,
  redirecting: false
}

const slice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    fullReset () {
      return initialState
    },
    pageUpdated (state, action) {
      if (state.page !== action.payload) {
        state.previousPage = state.page
        state.page = action.payload
      }
    },
    pageUpdatedToDefault (state, action) {
      state.previousPage = state.page
      state.page = state.defaultPage
    },
    appReady (state) {
      state.ready = true
    },
    externalScreenConnected (state, action) {
      state.externalScreenConnected = !!action.payload
    },
    modeUpdated (state, action) {
      state.mode = action.payload
    },
    optionsUpdated (state, action) {
      Object.assign(state.options, action.payload)
    },
    roleUpdated (state, action) {
      state.role = action.payload
      state.defaultPage = getDefaultPage(state.role)
    },
    toggledL1 (state, action) {
      state.showL1 = !!action.payload
    },
    toggledL2 (state, action) {
      state.showL2 = !!action.payload
    },
    openL2 (state, action) {
      state.openL2 = !!action.payload
    },
    notificationDismissed (state, action) {
      state.notification = initialNotification()
    },
    notificationDispatched (state, action) {
      const { type, message, content } = action.payload
      state.notification = { type, message, content }
    },
    actionClosed (state, action) {
      state.action.opened = false
    },
    actionOpened (state, action) {
      const { name, args = {} } = action.payload
      state.action.args = args
      state.action.name = name
      state.action.opened = true
    },
    dialogClosed (state, action) {
      state.dialog.opened = false
    },
    dialogOpened (state, action) {
      const { name, args = {} } = action.payload
      state.dialog.args = args
      state.dialog.name = name
      state.dialog.opened = true
    },
    markRedirecting (state, action) {
      state.redirecting = true
    }
  },
  extraReducers (builder) {
    builder
      // copying a single item
      .addCase(explorer.duplicateItem.rejected, (state, action) => {
        const entityId = action.meta.arg
        const itemType = selectItemType(entityId)
        state.notification.type = notificationTypes.error
        state.notification.message = `Failed to duplicate ${itemType}.`
        state.notification.content = undefined
      })
      .addCase(explorer.duplicateItem.fulfilled, (state, action) => {
        const entityId = action.meta.arg
        const itemType = selectItemType(entityId)
        const label = itemType[0].toUpperCase() + itemType.substring(1)
        state.notification.type = notificationTypes.success
        state.notification.message = `${label} duplicated successfully.`
        state.notification.content = undefined
      })

    // bulk action for copying items
      .addCase(explorer.copyItems.rejected, (state, action) => {
        const { targetEntity, entityIds } = action.meta.arg
        const subject = pluralize(entityIds.length, 'item',
          { includeNumber: true })
        state.notification.type = notificationTypes.error
        state.notification.message = `Error copying ${subject} to "${targetEntity.name}".`
        state.notification.content = undefined
      })
      .addCase(explorer.copyItems.fulfilled, (state, action) => {
        const { targetEntity, entityIds } = action.meta.arg
        const subject = pluralize(entityIds.length, 'item',
          { includeNumber: true })
        state.notification.type = notificationTypes.success
        state.notification.message = `${subject} copied to "${targetEntity.name}".`
        state.notification.content = undefined
      })

      .addCase(explorer.trashItems.rejected, (state, action) => {
        const entityIds = action.meta.arg
        const subject = pluralize(entityIds.length, 'item',
          { includeNumber: true })
        state.notification.type = notificationTypes.error
        state.notification.message = `Error moving ${subject} to trash.`
        state.notification.content = undefined
      })
      .addCase(explorer.trashItems.fulfilled, (state, action) => {
        const entityIds = action.meta.arg
        const subject = pluralize(entityIds.length, 'item',
          { includeNumber: true })
        state.notification.type = notificationTypes.success
        state.notification.message = `${subject} moved to the trash.`
        state.notification.content = undefined
      })

      .addCase(explorer.deleteItems.rejected, (state, action) => {
        const entityIds = action.meta.arg
        const subject = pluralize(entityIds.length, 'item',
          { includeNumber: true })
        state.notification.type = notificationTypes.error
        state.notification.message = `Error deleting ${subject}.`
        state.notification.content = undefined
      })
      .addCase(explorer.deleteItems.fulfilled, (state, action) => {
        const entityIds = action.meta.arg
        const subject =
          pluralize(entityIds.length, 'item', { includeNumber: true })
        state.notification.type = notificationTypes.success
        state.notification.message = `${subject} permanently deleted.`
        state.notification.content = undefined
      })

      .addCase(thunks.checkFullyLoggedIn.pending, (state) => {
        state.secondaryDomainAuth = secondaryDomainAuthStatuses.pending
      })
      .addCase(thunks.checkFullyLoggedIn.rejected, (state, action) => {
        window.Rollbar?.error('Customs login check error', action)
        state.secondaryDomainAuth = secondaryDomainAuthStatuses.failed
      })
      .addCase(thunks.checkFullyLoggedIn.fulfilled, (state, action) => {
        state.secondaryDomainAuth = action.payload ? secondaryDomainAuthStatuses.ok : secondaryDomainAuthStatuses.failed
      })
  }
})

const sliceExport = {
  name: slice.name,
  reducer: slice.reducer,
  ...slice.actions,
  ...selectors,
  ...thunks
}

export { sliceExport as slice }
